import { AppState } from "../types";
import { Opportunity } from "../types/opportunity";
import { RiskLevel } from "../types/common";

export type OpsSignal = {
  opportunity_id: string;
  company_name: string;
  role_title: string;
  priority: RiskLevel;
  open_escalations: number;
  blocked_tasks: number;
  urgency_score: number;
};

export type GlobalOpsSnapshot = {
  signals: OpsSignal[];
  total_active_opportunities: number;
  total_open_escalations: number;
  critical_incidents: number;
};

export function calculateUrgencyScore(opportunity: Opportunity, state: AppState): number {
  let score = 0;

  // Base priority score
  const priorityWeights: Record<RiskLevel, number> = {
    none: 0,
    low: 10,
    medium: 30,
    high: 60,
    critical: 100,
  };
  score += priorityWeights[opportunity.priority] || 0;

  // Escalations add weight
  const oppEscalations = state.escalations.filter(
    (e) =>
      e.opportunity_id === opportunity.opportunity_id &&
      e.status !== "closed" &&
      e.status !== "resolved",
  );
  score += oppEscalations.length * 20;

  // Blocked tasks add weight
  const blockedTasks = state.tasks.filter(
    (t) => t.opportunity_id === opportunity.opportunity_id && t.status === "blocked",
  );
  score += blockedTasks.length * 10;

  // Stage weight (later stages are more urgent)
  const stageWeights: Record<string, number> = {
    interview_active: 40,
    offer_review: 50,
    positioning: 20,
  };
  score += stageWeights[opportunity.current_stage] || 0;

  return score;
}

export function getGlobalOpsSnapshot(state: AppState): GlobalOpsSnapshot {
  const activeOpportunities = state.opportunities.filter((o) => o.status === "active");

  const signals: OpsSignal[] = activeOpportunities.map((opp) => {
    const openEscalations = state.escalations.filter(
      (e) =>
        e.opportunity_id === opp.opportunity_id && e.status !== "closed" && e.status !== "resolved",
    ).length;

    const blockedTasks = state.tasks.filter(
      (t) => t.opportunity_id === opp.opportunity_id && t.status === "blocked",
    ).length;

    return {
      opportunity_id: opp.opportunity_id,
      company_name: opp.company_name,
      role_title: opp.role_title,
      priority: opp.priority,
      open_escalations: openEscalations,
      blocked_tasks: blockedTasks,
      urgency_score: calculateUrgencyScore(opp, state),
    };
  });

  // Sort by urgency score descending
  signals.sort((a, b) => b.urgency_score - a.urgency_score);

  const totalOpenEscalations = state.escalations.filter(
    (e) => e.status !== "closed" && e.status !== "resolved",
  ).length;

  const criticalIncidents = signals.filter(
    (s) => s.priority === "critical" || s.open_escalations > 0,
  ).length;

  return {
    signals,
    total_active_opportunities: activeOpportunities.length,
    total_open_escalations: totalOpenEscalations,
    critical_incidents: criticalIncidents,
  };
}

/**
 * Explicit sequencing logic for the solo operator.
 * When multiple incidents collide, this returns the recommended order of attention.
 */
export function getRecommendedSequencing(state: AppState): OpsSignal[] {
  const snapshot = getGlobalOpsSnapshot(state);
  return snapshot.signals;
}
